import { Table, TableBody, TableRow, TableHead, TableContainer, TableCell } from '@mui/material'
import { makeStyles, styled } from '@mui/styles'
import { Vendor } from 'api/vendors'
import EditField from 'components/DocumentDetail/Editor/EditField'
import EditRow from 'components/DocumentDetail/Editor/EditRow'
import VendorItemRow from 'components/DocumentDetail/Editor/VendorItemRow'
import VendorNameRow from 'components/DocumentDetail/Editor/VendorNameRow'
import { Field } from 'components/DocumentDetail/types'
import { useRootLoaderData } from 'components/Root.loader'
import FullHeightFlexContainer from 'components/ui/FullHeightFlexContainer'
import { DocumentInvoiceV2 } from 'entities/DocumentsInvoiceV2'
import getInvoiceFields from 'entities/getInvoiceFields'
import { useMemo } from 'react'
import { ValidationValueMessage } from 'react-hook-form'

interface Props {
  docDetail: DocumentInvoiceV2
  vendors: Vendor[]
}

const SubTitleCell = styled(TableCell)({
  fontWeight: 600,
})

const useStyles = makeStyles({
  fixedTable: {
    tableLayout: 'fixed',
  },
  labelCell: {
    width: '40%',
  },
})

function Base({ docDetail, vendors }: Props) {
  const classes = useStyles()
  const { docInfo, config: docConfig } = docDetail
  const { isVerifyRegistered } = docInfo
  const rootData = useRootLoaderData()
  const userGroupConfig = rootData.userGroupConfig
  const currentFields = useMemo(
    () => getInvoiceFields(docDetail, userGroupConfig, rootData.expenseTypesOptions),
    [docDetail, userGroupConfig, rootData],
  )
  const { useConcur } = userGroupConfig
  const isChecking = docDetail.isChecking

  const genRows = (title, fields: Field[]) => {
    if (!fields.length) return null

    let hasDisplay = false
    const hiddenStyle = { display: 'none' }

    const rows = fields.map((field) => {
      const required =
        field.rules?.required === true ||
        !!(field.rules?.required as ValidationValueMessage<boolean>)?.value
      const disabled = !docDetail.canEdit() || field.compProps.disabled
      /**
       * - only show when config[key].display is true
       * - hide if field is not verifiable item during checking
       */
      let style
      if (
        field.config?.display === false ||
        (!isVerifyRegistered && field.config?.verify === false && isChecking)
      ) {
        style = hiddenStyle
      } else {
        hasDisplay = true
      }
      const hlKey = `${field.name}\\.`
      return (
        <EditRow style={style} key={field.name} label={field.label} required={required}>
          <EditField {...field} compProps={{ ...field.compProps, disabled }} hlKey={hlKey} />
        </EditRow>
      )
    })

    return (
      <>
        <TableRow style={hasDisplay ? undefined : hiddenStyle}>
          <SubTitleCell colSpan={2}>{title}</SubTitleCell>
        </TableRow>
        {rows}
      </>
    )
  }

  const genVendorRows = (vendors: Vendor[]) => {
    let hasDisplay = false
    const hiddenStyle = { display: 'none' }
    const rows = currentFields.headerBillFrom.map((field) => {
      /**
       * - only show when config[key].display is true
       * - hide if field is not verifiable item during checking
       */
      let style
      if (
        field.config?.display === false ||
        (!isVerifyRegistered && field.config?.verify === false && isChecking)
      ) {
        style = hiddenStyle
      } else {
        hasDisplay = true
      }
      if (field.name === 'headerBillFrom.billFromCompanyName') {
        const disabled = !docDetail.canEdit() || field.compProps.disabled
        return (
          <VendorNameRow
            name={field.name}
            defaultValue={docDetail.ocrEditResult.headerBillFrom.billFromCompanyName}
            label={field.label || ''}
            style={style}
            key={field.name}
            document={docDetail}
            docConfig={docConfig}
            compProps={{ ...field.compProps, disabled }}
            vendors={vendors}
          />
        )
      }
      return <VendorItemRow style={style} key={field.name} {...field} />
    })

    return (
      <>
        <TableRow style={hasDisplay ? undefined : hiddenStyle}>
          <SubTitleCell colSpan={2}>請求元企業情報</SubTitleCell>
        </TableRow>
        {rows}
      </>
    )
  }

  return (
    <FullHeightFlexContainer>
      <TableContainer>
        <Table
          stickyHeader
          size={'medium'}
          className={classes.fixedTable}
          aria-label="base fields table"
        >
          <TableHead data-testid="table-head-base-edit">
            <TableRow>
              <TableCell className={classes.labelCell}>項目名</TableCell>
              <TableCell>編集結果</TableCell>
            </TableRow>
          </TableHead>
          <TableBody data-testid="table-body-base-edit">
            {genRows('一般情報', currentFields.headerBase)}

            {useConcur
              ? genVendorRows(vendors)
              : genRows('請求元企業情報', currentFields.headerBillFrom)}

            {genRows('請求先情報', currentFields.headerBillTo)}

            {genRows('振込先情報', currentFields.headerBank)}

            {currentFields.headerContact !== undefined
              ? genRows('問合せ先情報', currentFields.headerContact)
              : undefined}

            {genRows('その他', currentFields.other)}
          </TableBody>
        </Table>
      </TableContainer>
    </FullHeightFlexContainer>
  )
}

export default Base
